import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { authedFetch } from '../api'

export interface ResultLog {
  id: string
  container_name: string
  timestamp: string
  line: string
  phase?: 'fixing' | 'validating'
}

export interface ResultEvent {
  id: string
  type: 'task.started' | 'task.done'
  timestamp: string
  phase: 'fixing' | 'validating'
  data: Record<string, unknown>
}

export interface RunResult {
  task_id: string
  failure_id: string
  failure_title: string | null
  failure_short_id: string | null
  status: 'pass' | 'fail' | 'dsq' | 'disagree' | 'pending' | 'not_started' | 'fixing' | 'fix_completed' | 'validating'
  llm_assessment: string
  dsq_claude_result: string
  dsq_claude: string
  dsq_codex_result: string
  dsq_codex: string
  grade_claude_result: string
  grade_claude: string
  grade_codex_result: string
  grade_codex: string
  root_cause_judge_result: string
  root_cause_judge: string
  cost: number
  time: number
  complexity: string
  score: number
  logs: ResultLog[]
  events: ResultEvent[]
  log_count: number
  fix_started_at: string | null
  verify_started_at: string | null
  assessed_at: string | null
  agent_id: string | null
  agent_name: string | null
  attempt: number
  container_name: string | null
}

export interface RunAgent {
  agent_id: string
  agent_name: string
}

export interface RunLog {
  id: string
  container_name: string
  timestamp: string
  line: string
}

export interface RunDetail {
  id: string
  name: string
  status: string
  error_message: string
  agent_name: string
  agent_tool_command: string
  agent_prompt_version: string
  failure_count: number
  attempts_count: number
  refined: boolean
  selected_failures: string[]
  tags: string[]
  agents: RunAgent[]
  branch: string
  commit_hash: string
  started_at: string
  finished_at: string | null
  success_rate: number | null
  avg_cost: number | null
  avg_time: number | null
  results: RunResult[]
  logs: RunLog[]
  containers: string[]
}

export interface TaskLogsEntry {
  logs: ResultLog[]
  events: ResultEvent[]
}

interface RunDetailsState {
  current: RunDetail | null
  loading: boolean
  error: string | null
  taskLogs: Record<string, TaskLogsEntry>
}

const initialState: RunDetailsState = {
  current: null,
  loading: false,
  error: null,
  taskLogs: {},
}

export const fetchRunDetails = createAsyncThunk(
  'runDetails/fetchRunDetails',
  async (runId: string) => {
    const response = await fetch(`/api/runs/${runId}`)
    if (!response.ok) throw new Error('Failed to fetch run details')
    return response.json() as Promise<RunDetail>
  }
)

export const fetchResultLogs = createAsyncThunk(
  'runDetails/fetchResultLogs',
  async ({ runId, taskId }: { runId: string; taskId: string }) => {
    const response = await fetch(`/api/runs/${runId}/logs/${taskId}`)
    if (!response.ok) throw new Error('Failed to fetch logs')
    const data = await response.json() as { logs: ResultLog[]; events: ResultEvent[] }
    return { taskId, logs: data.logs, events: data.events }
  }
)

export const regenerateResults = createAsyncThunk(
  'runDetails/regenerateResults',
  async (runId: string) => {
    const response = await authedFetch(`/api/runs/${runId}/regenerate`, { method: 'POST' })
    if (!response.ok) throw new Error('Failed to regenerate results')
    return response.json()
  }
)

export const rerunAssessments = createAsyncThunk(
  'runDetails/rerunAssessments',
  async (taskIds: string[]) => {
    const res = await authedFetch('/api/rerun-assessments', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ task_ids: taskIds }),
    })
    if (!res.ok) {
      throw new Error('Failed to trigger assessment rerun')
    }
    return taskIds.map((task_id) => ({ task_id, ok: true }))
  }
)

const runDetailsSlice = createSlice({
  name: 'runDetails',
  initialState,
  reducers: {
    clearRunDetails: (state) => {
      state.current = null
      state.loading = false
      state.error = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchRunDetails.pending, (state) => {
        if (!state.current) state.loading = true
        state.error = null
      })
      .addCase(fetchRunDetails.fulfilled, (state, action) => {
        state.loading = false
        // Preserve already-loaded logs from the previous state so that
        // polling refreshes don't wipe out expanded log data.
        if (state.current) {
          const prevLogsMap = new Map<string, ResultLog[]>()
          const prevEventsMap = new Map<string, ResultEvent[]>()
          for (const r of state.current.results) {
            if (r.logs && r.logs.length > 0) {
              prevLogsMap.set(r.task_id, r.logs)
            }
            if (r.events && r.events.length > 0) {
              prevEventsMap.set(r.task_id, r.events)
            }
          }
          for (const r of action.payload.results) {
            if ((!r.logs || r.logs.length === 0) && prevLogsMap.has(r.task_id)) {
              r.logs = prevLogsMap.get(r.task_id)!
            }
            if ((!r.events || r.events.length === 0) && prevEventsMap.has(r.task_id)) {
              r.events = prevEventsMap.get(r.task_id)!
            }
          }
        }
        state.current = action.payload
      })
      .addCase(fetchRunDetails.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message ?? 'Failed to fetch run details'
      })
      .addCase(rerunAssessments.fulfilled, (state, action) => {
        if (state.current) {
          const succeededTaskIds = new Set(
            action.payload.filter(r => r.ok).map(r => r.task_id)
          )
          for (const result of state.current.results) {
            if (succeededTaskIds.has(result.task_id)) {
              result.status = 'validating'
            }
          }
        }
      })
      .addCase(fetchResultLogs.fulfilled, (state, action) => {
        // Always store in taskLogs map so any page can access them
        state.taskLogs[action.payload.taskId] = {
          logs: action.payload.logs,
          events: action.payload.events,
        }
        if (state.current) {
          // Also set logs on the specific task by task_id for run details page
          for (const result of state.current.results) {
            if (result.task_id === action.payload.taskId) {
              result.logs = action.payload.logs
              result.events = action.payload.events
            }
          }
        }
      })
  },
})

export const { clearRunDetails } = runDetailsSlice.actions
export default runDetailsSlice.reducer
